import { Input, InputGroup, InputRightElement, IconButton } from '@chakra-ui/react'
import { CloseIcon } from '@chakra-ui/icons'
import type {
  FieldErrors,
  RegisterOptions,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import type { LocationProfileContactFormValues } from '../../../types/location'
import {
  INVALID_EMAIL_MESSAGE,
  isValidEmailFormat,
} from '../../../utils/validation-helpers/emailFormatValidate'
import {
  INVALID_PHONE_MESSAGE,
  isPhoneNumberValid,
} from '../../../utils/validation-helpers/mobileNumberFormatValidate'
import { validateOptionalProviderNumberTrim } from '../../../utils/validation-helpers/ProviderNumberValidate'
import { LocationFormField } from './LocationFormField'

const LOCATION_PROFILE_INPUT_STYLES = {
  height: '42px',
  borderRadius: 'md',
  _placeholder: { color: 'gray.400' },
} as const

const PROVIDER_NUMBER_RULES: RegisterOptions<LocationProfileContactFormValues, 'providerNumber'> = {
  validate: validateOptionalProviderNumberTrim,
}

const PHONE_RULES: RegisterOptions<LocationProfileContactFormValues, 'phoneNumber'> = {
  validate: (value) => !value?.trim() || isPhoneNumberValid(value.trim()) || INVALID_PHONE_MESSAGE,
}

const EMAIL_RULES: RegisterOptions<LocationProfileContactFormValues, 'email'> = {
  validate: (value) => !value?.trim() || isValidEmailFormat(value.trim()) || INVALID_EMAIL_MESSAGE,
}

type LocationProfileContactFieldsProps = {
  register: UseFormRegister<LocationProfileContactFormValues>
  setValue: UseFormSetValue<LocationProfileContactFormValues>
  watch: UseFormWatch<LocationProfileContactFormValues>
  errors: FieldErrors<LocationProfileContactFormValues>
}

export const LocationProfileContactFields = ({
  register,
  setValue,
  watch,
  errors,
}: LocationProfileContactFieldsProps) => {
  const providerReg = register('providerNumber', PROVIDER_NUMBER_RULES)
  const phoneReg = register('phoneNumber', PHONE_RULES)
  const emailReg = register('email', EMAIL_RULES)

  const providerNumberValue = watch('providerNumber')
  const phoneValue = watch('phoneNumber')
  const emailValue = watch('email')

  return (
    <>
      <LocationFormField
        label="Provider No"
        placeholder="e.g. 2345678A"
        registration={providerReg}
        error={errors.providerNumber?.message}
      >
        <InputGroup>
          <Input
            id="providerNumber"
            placeholder="e.g. 2345678A"
            {...LOCATION_PROFILE_INPUT_STYLES}
            {...providerReg}
          />
          {providerNumberValue ? (
            <InputRightElement>
              <IconButton
                aria-label="Clear provider number"
                icon={<CloseIcon boxSize={2.5} />}
                size="xs"
                variant="ghost"
                isRound
                onClick={() =>
                  setValue('providerNumber', '', { shouldValidate: true, shouldDirty: true })
                }
                color="gray.400"
                _hover={{ bg: 'gray.100', color: 'gray.600' }}
              />
            </InputRightElement>
          ) : null}
        </InputGroup>
      </LocationFormField>

      <LocationFormField
        label="Phone"
        placeholder="e.g. 0411222333"
        type="tel"
        registration={phoneReg}
        error={errors.phoneNumber?.message}
      >
        <InputGroup>
          <Input
            id="phoneNumber"
            type="tel"
            placeholder="e.g. 0411222333"
            {...LOCATION_PROFILE_INPUT_STYLES}
            {...phoneReg}
          />
          {phoneValue ? (
            <InputRightElement>
              <IconButton
                aria-label="Clear phone"
                icon={<CloseIcon boxSize={2.5} />}
                size="xs"
                variant="ghost"
                isRound
                onClick={() =>
                  setValue('phoneNumber', '', { shouldValidate: true, shouldDirty: true })
                }
                color="gray.400"
                _hover={{ bg: 'gray.100', color: 'gray.600' }}
              />
            </InputRightElement>
          ) : null}
        </InputGroup>
      </LocationFormField>

      <LocationFormField
        label="Email"
        placeholder="example@email.com"
        type="email"
        registration={emailReg}
        error={errors.email?.message}
      >
        <InputGroup>
          <Input
            id="email"
            type="email"
            placeholder="example@email.com"
            {...LOCATION_PROFILE_INPUT_STYLES}
            {...emailReg}
          />
          {emailValue ? (
            <InputRightElement>
              <IconButton
                aria-label="Clear email"
                icon={<CloseIcon boxSize={2.5} />}
                size="xs"
                variant="ghost"
                isRound
                onClick={() => setValue('email', '', { shouldValidate: true, shouldDirty: true })}
                color="gray.400"
                _hover={{ bg: 'gray.100', color: 'gray.600' }}
              />
            </InputRightElement>
          ) : null}
        </InputGroup>
      </LocationFormField>
    </>
  )
}
